import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { SplashScreen } from '@capacitor/splash-screen'
import { StatusBar, Style } from '@capacitor/status-bar'
import { handleNativeAuthCallbackUrl } from './authDeepLinks'
import { handleNativeBrokerSyncCallbackUrl } from './brokerSyncDeepLinks'

let initialized = false
let nativeSplashHidden = false
const NATIVE_STATUS_BAR_HEIGHT_VAR = '--native-status-bar-height'
const NATIVE_DEEP_LINK_DEDUP_MS = 2000
const nativeDeepLinkTasks = new Map<string, Promise<void>>()

export function isNativeMobile() {
  return Capacitor.isNativePlatform()
}

export function getNativePlatform() {
  return Capacitor.getPlatform()
}

export function isNativeIOS() {
  return isNativeMobile() && Capacitor.getPlatform() === 'ios'
}

export function isNativeAndroid() {
  return isNativeMobile() && Capacitor.getPlatform() === 'android'
}

export async function hideNativeSplash() {
  if (!isNativeMobile() || nativeSplashHidden) return

  try {
    await SplashScreen.hide({ fadeOutDuration: 120 })
    nativeSplashHidden = true
  } catch (error) {
    console.warn('[Native] Failed to hide the launch screen:', error)
  }
}

async function syncStatusBar() {
  const forceLightMode = document.documentElement.classList.contains('native-welcome-light')
  const forceDarkMode = document.documentElement.classList.contains('native-welcome-dark')
  const isDarkMode =
    forceDarkMode || (!forceLightMode && document.documentElement.classList.contains('dark'))

  try {
    await StatusBar.setStyle({ style: isDarkMode ? Style.Dark : Style.Light })
  } catch {
    // Status bar calls are best-effort because availability differs by platform and OS version.
  }

  if (isNativeAndroid()) {
    try {
      await StatusBar.setOverlaysWebView({ overlay: false })
    } catch {
      // Android 15+ can force edge-to-edge; CSS inset compensation below handles that case.
    }

    try {
      await StatusBar.setBackgroundColor({ color: isDarkMode ? '#020617' : '#ffffff' })
    } catch {
      // Background color is unavailable when the status bar overlays the webview.
    }
  }

  await updateNativeStatusBarInset()
}

function setNativeStatusBarHeight(height: number) {
  document.documentElement.style.setProperty(
    NATIVE_STATUS_BAR_HEIGHT_VAR,
    `${Math.max(0, Math.round(height))}px`
  )
}

async function updateNativeStatusBarInset() {
  if (!isNativeMobile()) {
    setNativeStatusBarHeight(0)
    return
  }

  try {
    const info = await StatusBar.getInfo()
    setNativeStatusBarHeight(info.visible && info.overlays ? info.height : 0)
  } catch {
    setNativeStatusBarHeight(0)
  }
}

function configureBackButton() {
  void App.addListener('backButton', async ({ canGoBack }) => {
    // Search consumes Back only while open; all other screens retain this handler.
    if (!window.dispatchEvent(new Event('optioneer:native-back', { cancelable: true }))) return
    if (canGoBack || window.history.length > 1) {
      window.history.back()
      return
    }

    if (isNativeAndroid()) {
      try {
        await App.minimizeApp()
      } catch {
        App.exitApp()
      }
    }
  })
}

function handleNativeDeepLink(url: string) {
  const existingTask = nativeDeepLinkTasks.get(url)
  if (existingTask) return existingTask

  // Cold-start scene URLs arrive through both the retained appUrlOpen event
  // and getLaunchUrl. PKCE codes are single use, so share in-flight work and
  // briefly retain completed deliveries without blocking later sign-in attempts.
  const task: Promise<void> = Promise.resolve().then(async () => {
    try {
      if (!(await handleNativeBrokerSyncCallbackUrl(url))) {
        await handleNativeAuthCallbackUrl(url)
      }
    } catch {
      nativeDeepLinkTasks.delete(url)
      // Underlying errors can include OAuth callback tokens or authorization codes.
      console.warn('[Native] Unable to handle the app link. Please try again.')
      return
    }

    window.setTimeout(() => {
      if (nativeDeepLinkTasks.get(url) === task) nativeDeepLinkTasks.delete(url)
    }, NATIVE_DEEP_LINK_DEDUP_MS)
  })
  nativeDeepLinkTasks.set(url, task)
  return task
}

function configureNativeDeepLinks() {
  void App.addListener('appUrlOpen', ({ url }) => {
    void handleNativeDeepLink(url)
  })

  void App.getLaunchUrl().then((launchUrl) => {
    if (launchUrl?.url) {
      void handleNativeDeepLink(launchUrl.url)
    }
  })
}

function configureNativeViewport() {
  const viewportContent = [
    'width=device-width',
    'initial-scale=1.0',
    'maximum-scale=1.0',
    'user-scalable=no',
    'viewport-fit=cover',
    'interactive-widget=resizes-content',
  ].join(', ')

  let viewport = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
  if (!viewport) {
    viewport = document.createElement('meta')
    viewport.name = 'viewport'
    document.head.appendChild(viewport)
  }

  viewport.content = viewportContent
}

export function initCapacitor() {
  if (initialized || !isNativeMobile()) {
    return
  }

  initialized = true
  document.documentElement.classList.add('capacitor-native')
  setNativeStatusBarHeight(0)

  configureNativeViewport()
  void syncStatusBar()
  configureBackButton()
  configureNativeDeepLinks()

  void StatusBar.addListener('statusBarOverlayChanged', (info) => {
    setNativeStatusBarHeight(info.visible && info.overlays ? info.height : 0)
  })
  void StatusBar.addListener('statusBarVisibilityChanged', (info) => {
    setNativeStatusBarHeight(info.visible && info.overlays ? info.height : 0)
  })

  const updateInset = () => {
    void updateNativeStatusBarInset()
  }
  window.addEventListener('resize', updateInset)
  window.addEventListener('orientationchange', updateInset)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      updateInset()
    }
  })

  const themeObserver = new MutationObserver(() => {
    void syncStatusBar()
  })

  themeObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })
}
